// EDEN · tiny i18n (no framework). EN is the source of truth; KM is type-checked against it,
// so a missing translation is a compile error. The Khmer below is a DRAFT authored for owner/
// translator review — see docs/KH_COPY_REVIEW.md (Design Brief: never auto-translate idioms,
// never ship unreviewed Khmer as final).
//
// Scope note: the rendered 5-stage plan TEMPLATE (stage titles, cell labels, empty prompts)
// comes from the server and stays English on purpose — the lesson plan itself is the teacher's
// own-English artifact (Module D rail). Only the UI chrome around it is bilingual.
// Lead/admin surfaces (status, insights, admin, dashboard, import, observation instrument)
// also stay English for the pilot.

import { esc } from "./dom";

const en = {
  // ── common chrome ──
  "common.loading": "Loading…",
  "common.back_home": "← Home",
  "common.back_this_week": "← This Week",
  "common.back_classes": "← My Classes",
  "common.back": "← Back",
  "common.week_n": "Week {n}",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.edit": "Edit",
  "common.remove": "Remove",
  "common.settings": "Settings",
  "common.sign_out": "Sign out",
  "common.print": "Print",
  "common.chip_draft": "Draft",
  "common.chip_submitted": "Submitted",

  // ── the Term 1 pilot label (chrome.ts: every screen, signed in or out); the owner approved these words 2026-09-27 ──
  "pilot.label": "Term 1 pilot: Term 1 materials are still being checked, and later terms are drafts. Tell your Lead about any problem.",

  // weekdays (calendar strips) + months
  "wd.0": "Sun", "wd.1": "Mon", "wd.2": "Tue", "wd.3": "Wed", "wd.4": "Thu", "wd.5": "Fri", "wd.6": "Sat",
  "mo.1": "Jan", "mo.2": "Feb", "mo.3": "Mar", "mo.4": "Apr", "mo.5": "May", "mo.6": "Jun",
  "mo.7": "Jul", "mo.8": "Aug", "mo.9": "Sep", "mo.10": "Oct", "mo.11": "Nov", "mo.12": "Dec",

  // calendar entry types (the chips)
  "type.teaching": "teaching", "type.test": "test", "type.event": "event", "type.holiday": "holiday",
  "type.training": "training",

  // ── offline ──
  "offline.title": "You're offline",
  "offline.body": "EDEN will reconnect on its own when you're back online. Nothing you've saved is lost.",
  "offline.retry": "Try again",
  "offline.signin": "You're offline. You'll be able to sign in once you're back online.",

  // ── autosave indicator ──
  "autosave.saving": "Saving…",
  "autosave.saved": "Saved · will sync",
  "autosave.offline": "Offline — your text will save when you're back online",
  "autosave.retry": "Not saved yet — will retry",

  // ── sign-in ──
  "signin.title": "Welcome back.",
  "signin.hint": "Sign in to plan tomorrow's lesson.",
  "signin.google_hint": "Admin: sign in with Google.<br/>Teachers: use your email + passcode below.",
  "signin.or": "or",
  "signin.email": "Email",
  "signin.passcode": "Passcode",
  "signin.button": "Sign in",

  // ── home ──
  "home.hello": "Hello, {name}.",
  "home.hint_first_plan_title": "New here? Start with one lesson.",
  "home.hint_first_plan": "Pick a class, then plan tomorrow's lesson — about ten minutes. EDEN keeps the thinking, you keep the teaching.",
  "home.hint_plan_link": "Plan a lesson",
  "home.hint_got_it": "Got it",
  "home.this_week": "This Week",
  "home.this_week_sub": "Your lessons for this week.",
  "home.changed_this_month": "Changed this month",
  "home.next_key_today": "Next: {label} · {date} · today",
  "home.next_key_tomorrow": "Next: {label} · {date} · tomorrow",
  "home.next_key_days": "Next: {label} · {date} · in {n} days",
  "home.classes": "My Classes",
  "home.classes_sub": "The classes you teach.",
  "home.observations": "My observations",
  "home.observations_sub": "Your observation records, discussed 1-on-1.",
  "home.status": "Department Status",
  "home.status_sub": "See who has planned what this week.",
  "home.insights": "Insights",
  "home.insights_sub": "Each teacher's term picture + your tracking notes.",
  "home.accounts": "Accounts",
  "home.accounts_sub": "Add and manage teacher accounts.",
  "home.dashboard": "Dashboard",
  "home.dashboard_sub": "This week's deadlines and the team's projects.",
  "home.calendar": "School Calendar",
  "home.calendar_sub": "Term dates, tests, and school events.",
  "home.tests": "Tests & Worksheets",
  "home.tests_sub": "Make a test or worksheet to print. EDEN checks it against the department's test rules.",
  "home.view_week": "View week →",
  "home.reference": "Reference",
  "home.lead_section": "Lead",

  // ── nav chrome (desktop sidebar + mobile tab bar + hub, all from src/nav.ts) ──
  "nav.teaching": "Teaching",
  "nav.coordination": "Coordination",
  "nav.account": "Account",
  "nav.account_sub": "Change your passcode.",
  "nav.leadership": "Leadership",
  "nav.role_teacher": "Teacher",
  "nav.role_lead": "Lead",
  "nav.more": "More",
  "nav.close": "Close",
  "nav.crumb_home": "Home",
  "nav.related": "Related",
  "nav.class_lessons": "Lessons in this class",
  "nav.class_roster": "Class & roster",
  "nav.see_full_calendar": "See full calendar →",
  "nav.plan_for_class": "Plan a lesson for this class",
  "nav.tab_week": "This Week",
  "nav.tab_classes": "Classes",
  "nav.tab_calendar": "Calendar",
  "nav.tab_roadmap": "Roadmap",
  "nav.tab_coach": "Coach",
  "nav.tab_dept": "Department",
  "home.roadmap": "Roadmap",
  "home.roadmap_sub": "The whole year, week by week.",
  "home.coach": "Coach",
  "home.coach_sub": "Talk a plan through — no scores, ever.",
  "home.department": "Department",
  "home.department_sub": "Plans waiting for you, and how each class is moving through the year.",

  // ── Department board (Lead) — approvals inbox + pacing (IA increment 4). Plain and neutral:
  //    gray never red, information never ranking; explain each concept once, in short sentences.
  "dept.approvals": "Approvals",
  "dept.waiting_none": "Nothing is waiting — every submitted plan has been reviewed.",
  "dept.waiting_one": "1 plan is waiting for your review.",
  "dept.waiting_n": "{n} plans are waiting for your review.",
  "dept.pacing": "Pacing — coordination, not a scoreboard",
  "dept.pacing_hint": "Each row shows where a class is on its curriculum map next to the template's suggestion for this week, and its projected year-end coverage (the promise is ≥95%). Planning aids for conversations — never a ranking.",
  "dept.no_classes": "No classes yet — the board draws itself from the department's classes.",
  "dept.step_line": "step {pos} · template suggests {tpl}",
  "dept.step_only": "step {pos} · no active term to compare against",
  "dept.on_pace": "on pace",
  "dept.within_buffer": "within buffer",
  "dept.behind_n": "{n} behind",
  "dept.ahead_n": "{n} ahead",
  "dept.no_links": "No plans are linked to curriculum steps yet — the projection starts with the first linked plan.",
  "dept.compress_n": "{n} compression options",
  "dept.compress_hint": "Steps that can merge to make room — buffer weeks first, then review steps. Retrieval starters continue regardless; thread steps are never dropped.",
  "dept.gate_pin": "{label} · T{term} wks {from}–{to}",
  "dept.plans_all": "All plans this term",
  "dept.footer": "Coverage figures are planning aids for coaching conversations — never published to the team. A partial picture, not the whole story.",

  // ── week v1 (the node card — "the spine is the screen") ──
  "week.on_map": "On the map this week",
  "week.position": "Roadmap position",
  "week.node_of": "node {n} of {total}",
  "week.no_curriculum": "No curriculum map for this class yet.",
  "week.no_grade": "Name the class with its grade (e.g. “G6A”) to connect the curriculum map.",
  "week.grammar": "Grammar",
  "week.thread": "Thread",
  "week.task": "Exam task",
  "week.fluency": "Fluency",
  "week.reading": "Reading",
  "week.rfs_pending": "Reading for Sure — slot reserved [pending contents photo]",
  "week.open_roadmap": "See the roadmap →",
  "week.class_label": "Class",

  // ── materials (the Teacher Pack + exercise sheet behind a node) ──
  "mat.eyebrow": "Ready for this week",
  "mat.pack": "Teacher Pack (PDF)",
  "mat.sheet": "Exercise sheet",
  "mat.sheet_pending": "Exercise sheet — not written for this week yet",
  "mat.none": "No Teacher Pack for this step yet.",
  "mat.coursebook": "Coursebook",
  "mat.plan_link": "Open this step's Teacher Pack — the materials are listed inside it.",
  // Sheet-only grades (G8-G10): owner ruling 4, 2026-09-05, retired their PDF pack fleet, so the
  // exercise sheet is the whole of what is served for these steps. Say so plainly rather than
  // leaving a gap that reads as a missing file.
  "mat.no_pack": "No Teacher Pack for this grade — the exercise sheet is the printable material.",
  "mat.plan_link_sheet": "Open this step's exercise sheet — this grade has no Teacher Pack PDF.",
  "roadmap.pack_mark": "pack",
  "roadmap.sheet_mark": "sheet",
  "roadmap.materials_legend": "{packs} packs · {sheets} exercise sheets ready for this grade",
  "roadmap.materials_legend_sheets_only": "{sheets} exercise sheets ready for this grade — no Teacher Pack PDFs for it",

  // ── resources (WEB-P2: pins + citation graph, ranked, on the Week card) ──
  "res.eyebrow": "Resources",
  "res.use_this": "Use this",
  "res.in_use": "In use",
  "res.more": "Show {n} more",

  // ── period slot (WEB-P3: the lesson is node/strand/ordinal, not just a node) ──
  "slot.period": "Period",
  "slot.of_n": "of {n}",
  "slot.strand_coursebook": "Coursebook",
  "slot.strand_thread": "Cambridge thread",
  "slot.strand_fluency": "Fluency & reading",
  "slot.eyebrow": "Resources for one period",
  "slot.browse_all": "Show all {n}",
  "slot.no_edges": "No resources are listed for this part of the week yet — here is everything for the week ({n}).",
  "slot.classroom_activity": "Classroom activity",
  "slot.make_worksheet": "Make worksheet →",

  // ── roadmap ──
  "roadmap.eyebrow_fallback": "Your year",
  "roadmap.countable": "{total} countable nodes · coverage floor ≥95% ⇒ ≥{floor}",
  "roadmap.template_note": "Position shown is the template's suggestion for this week — your class's own position arrives when plans link to nodes.",
  "roadmap.no_class": "Add a class first — the roadmap draws itself from the class's grade.",
  "roadmap.no_term": "No active term in the calendar — the roadmap shows the full year.",
  "roadmap.you_are_here": "you are here",
  "roadmap.gate_year": "gate year",
  "roadmap.term_n": "Term {n}",

  // ── coach hub ──
  "coach.talk_title": "Talk this week's plan through",
  "coach.talk_sub": "5 minutes, out loud, in English — the coach asks about your week's node. No scores, ever.",
  "coach.talk_cta": "Start a talk-through",
  "coach.feedback_title": "Your English feedback",
  "coach.feedback_sub": "Notes from your Lead stay 1-on-1 — find them with your observations.",
  "coach.reflect_title": "After the lesson",
  "coach.reflect_sub": "A 60-second reflection joins the plan form in a later increment — for now, tell your next plan what actually happened.",

  // ── plan prefill · the class's map (increment 3 — plain, explanatory, never a gate) ──
  "plan.map_eyebrow": "On your class's map",
  "plan.map_hint": "We filled the topic below from this step — change anything.",
  "plan.map_pick": "Which step is this lesson for?",
  "plan.map_pick_hint": "Nearby steps are free to pick. Jumping further just asks for one line, so your Lead knows why.",
  "plan.map_unlink": "No step — plan free",
  "plan.map_note_label": "One line: why the change?",
  "plan.map_note_placeholder": "e.g. Games Day ate Wednesday, so we moved on",
  "plan.map_note_needed": "You're {n} steps from the suggestion — add one line so your Lead knows why. Then save away.",
  "plan.map_review_note": "This is a review step — a quick check that its sets were taught to this class helps.",
  "plan.strand": "Which part of the week is this lesson? (optional)",
  "plan.strand_skip": "Skip — not sure",
  "plan.strand_coursebook": "Coursebook hours",
  "plan.strand_thread": "Cambridge thread",
  "plan.strand_fluency": "Fluency & reading",

  // ── position lines (Week + Roadmap upgrade when plans carry steps) ──
  "week.next_step": "Your class's next step",
  "week.position_real": "your plans reach step {n} of {total}",
  "week.position_template": "this week's step: {n} of {total}",
  "roadmap.position_real": "Position from your plans: step {n}. Green dots are steps with a plan.",
  "roadmap.position_template": "No plans are linked to steps yet — this is the template's week. Plan from the Week tab and the map becomes your class's own.",

  // ── this week ──
  "week.title_fallback": "This Week",
  "week.preview": "Preview · Term {term} starts {date}",
  "week.at_school": "This week at school",
  "week.empty": "Nothing planned yet this week. Plan a lesson when you're ready.",
  "week.plan_button": "Plan a lesson",

  // ── fast-capture + edit (form chrome; the typed plan itself stays the teacher's English) ──
  "plan.title": "Plan a lesson",
  "plan.edit_title": "Edit your plan",
  "plan.no_class": "Add a class first, then come back to plan a lesson.",
  "plan.add_class": "Add a class",
  "plan.class": "Class",
  "plan.week": "Week",
  "plan.topic": "Topic",
  "plan.topic_placeholder": "Talking about the weather",
  "plan.duration": "Duration (minutes)",
  "plan.thinking": "The thinking",
  "plan.thinking_hint": "Type these in your own words — paste is off here so it stays your English.",
  "plan.prior": "Prior knowledge (for this class)",
  "plan.prior_hint": "What did this class do last time that connects to this?",
  "plan.misconception": "The predictable misconception",
  "plan.misconception_hint": "What's the one thing they'll likely get wrong here?",
  "plan.cfu": "The check for understanding",
  "plan.cfu_hint": "How will you <em>see</em> that they got it, before you move on?",
  "plan.the_plan": "The plan",
  "plan.materials": "Objectives · Materials",
  "plan.materials_hint": "You can paste a vocab or materials list here.",
  "plan.procedure": "Lesson procedure (Steps 1–5)",
  "plan.procedure_hint": "Type the steps in your own words — paste is off here.",
  "plan.save_draft": "Save draft",
  "plan.submit": "Submit",
  "plan.save_submit": "Save & submit",
  "plan.save_resubmit": "Save & re-submit",
  "plan.topic_missing": "Add a topic so you can find this lesson again.",
  "plan.floor_cell": "Before this goes to your Lead, write a line or two of your own in “{label}” — what is true for this class?",
  "plan.floor_step": "Before this goes to your Lead, write out “{label}” in your own words — the key activities and what the students will actually do. Saving a draft is always fine.",
  "plan.locked": "This plan is approved and locked. Ask the Lead to re-open it to edit.",
  "plan.view_plan": "View the plan",

  // ── plan view (chrome around the rendered template) ──
  "view.title": "Here's your plan.",
  "view.planned_under_min": "Planned in under a minute.",
  "view.planned_in": "Planned in about {m} min.",
  "view.drift": "Edited after submitting — re-submit to update.",
  "view.edit": "Edit plan",
  "view.resubmit": "Re-submit",
  "view.submit": "Submit",
  "view.submitted": "Submitted ✓",
  "view.approve": "Approve",
  "view.approved_locked": "Approved ✓ · locked",
  "view.reopen": "Re-open to edit",
  "view.lead_note": "A note from your Lead",
  "view.lead_note_label": "Add a one-line note for the teacher (optional)",
  "view.lead_note_hint": "One kind line — the teacher sees it on this plan.",

  // ── classes + roster ──
  "classes.title": "My Classes",
  "classes.empty": "Add your first class to start planning.",
  "class.lessons": "Lessons in this class",
  "class.no_lessons": "No lessons planned for this class yet.",
  "class.only_week": "Showing Week {n}.",
  "class.show_all": "Show all weeks",
  // JOB-W01 · planned vs taught on the class page (taught = the class teacher's own confirmation).
  "class.planned_title": "Planned",
  "class.planned_count": "Lessons planned: {n}",
  "class.taught_title": "Taught — confirmed by the class teacher",
  "class.taught_count": "Sessions: {sessions} · Curriculum steps: {nodes}",
  "class.taught_freeform": "+ {n} not linked to a curriculum step",
  "class.withdrawn_count": "Withdrawn confirmations: {n}",
  "class.taught_hint": "A plan — even a submitted or approved one — is not a taught lesson. Repeat sessions of one step count once as a step.",
  "class.taught_only": "Showing taught lessons only.",
  "class.show_taught": "Show only taught lessons",
  "class.show_all_lessons": "Show all lessons",
  "class.no_taught": "No lessons confirmed as taught yet.",

  // ── JOB-W01 · the teaching record on a lesson (plan view) ──
  "teach.title": "Teaching record",
  "teach.hint": "Separate from Submit and Approve. Confirm only after you have taught this lesson to this class.",
  "teach.hint_reader": "The class teacher confirms this after teaching the lesson. It is separate from Submit and Approve.",
  "teach.loading": "Loading the teaching record…",
  "teach.load_error": "Couldn't load the teaching record. Reload the page to try again.",
  "teach.state_not_recorded": "Not recorded yet",
  "teach.state_taught": "Taught ✓",
  "teach.state_withdrawn": "Confirmation withdrawn",
  "teach.confirmed_on": "Confirmed on {when}",
  "teach.withdrawn_on": "Withdrawn on {when}",
  "teach.freeform": "This lesson isn't linked to a curriculum step, so it counts as a taught session but not as curriculum coverage.",
  "teach.confirm": "I taught this lesson",
  "teach.confirm_again": "Confirm taught again",
  "teach.week_not_started": "This lesson is planned for Week {n}, which starts on {date}. You can record it as taught from that day.",
  "teach.withdraw": "Withdraw confirmation",
  "teach.withdraw_label": "Why are you withdrawing it? (required)",
  "teach.withdraw_hint": "For example: recorded on the wrong lesson. The history keeps both entries.",
  "teach.withdraw_submit": "Withdraw confirmation",
  "teach.reason_required": "Write a short reason for withdrawing the confirmation.",
  "teach.read_only": "Only the class teacher can record this. You can see the history below.",
  "teach.history": "History",
  "teach.history_empty": "No teaching record yet.",
  "teach.event_confirm": "Confirmed taught",
  "teach.event_withdraw": "Withdrew confirmation",
  "teach.saved_confirm": "Recorded as taught.",
  "teach.saved_withdraw": "Confirmation withdrawn. The history keeps both entries.",
  "teach.replayed_other": "This was already recorded earlier, and the record has changed since. The latest record is shown.",
  "teach.jump": "Teaching record",
  "teach.offline": "We couldn't confirm this was saved. Check your connection and try again — trying again never records it twice.",

  // ── JOB-W02 · one lesson session, end to end (/lesson/:id) ──
  "week.open_session": "Open this lesson ›",
  "sess.title": "This lesson",
  "sess.loading": "Loading this lesson…",
  "sess.load_error": "Couldn't load this lesson. Reload the page to try again.",
  "sess.crumb": "Lesson",
  "sess.read_only": "You are reading this lesson. Only the class teacher can change it.",
  "sess.freeform": "This lesson isn't linked to a curriculum step. Its activities and papers are still recorded here.",
  "sess.step": "Curriculum step",
  "sess.period": "{strand} period {n}",
  "sess.plan_title": "Lesson plan",
  "sess.plan_open": "Open the plan",
  "sess.plan_start": "Write the plan",
  "sess.plan_none": "No plan written yet.",
  "sess.plan_status_not_started": "Not started",
  "sess.plan_status_in_progress": "Draft",
  "sess.plan_status_submitted": "Submitted",
  "sess.plan_status_approved": "Approved ✓",

  "sess.act_title": "Activities for this lesson",
  "sess.act_hint": "What you chose to use with this class, for this session. Choosing is not the same as teaching — the teaching record is below.",
  "sess.act_hint_reader": "What the class teacher chose to use for this session, and when it changed.",
  "sess.act_none": "Nothing chosen for this lesson yet.",
  "sess.act_none_reader": "The teacher hasn't recorded any activity for this lesson.",
  "sess.act_choose": "Use for this lesson",
  "sess.act_remove": "Remove",
  "sess.act_chosen": "In use",
  "sess.act_chosen_on": "Chosen {when}",
  "sess.act_changed_n": "changed {n} times",
  "sess.act_card": "Item Bank card {id} · version {short}",
  "sess.act_available": "Available for this step",
  "sess.act_available_hint": "From this curriculum step's resource list. Pick the ones you actually use.",
  "sess.act_available_none": "No resources are listed for this curriculum step yet.",
  "sess.act_browse_all": "Show all {n}",
  "sess.act_history": "What changed",
  "sess.act_history_empty": "Nothing chosen or removed yet.",
  "sess.act_event_select": "Chose",
  "sess.act_event_unselect": "Removed",
  "sess.act_saved_select": "Added to this lesson.",
  "sess.act_saved_unselect": "Removed from this lesson. The history keeps both entries.",
  "sess.act_replayed_other": "This was already recorded earlier, and the list has changed since. The latest list is shown.",
  "sess.act_offline": "We couldn't confirm this was saved. Check your connection and try again — trying again never records it twice.",
  "sess.standing": "You usually use {title} for this period.",
  "sess.standing_use": "Use it for this lesson",
  "sess.standing_hint": "Your usual pick for this period, across your classes. It is only recorded here once you add it.",

  "sess.paper_title": "Papers for this lesson",
  "sess.paper_hint": "Worksheets and tests you prepared for this session. A saved paper is not a taught lesson.",
  "sess.paper_hint_reader": "Papers the class teacher prepared for this session, with what each one is made of.",
  "sess.paper_none": "No paper prepared for this lesson yet.",
  "sess.paper_none_reader": "The teacher hasn't prepared a paper for this lesson.",
  "sess.paper_new": "Make a worksheet for this lesson",
  "sess.paper_new_from_this": "Make a worksheet from this",
  "sess.paper_new_hint": "It opens in the editor, already linked to this lesson.",
  "sess.paper_new_from": "Starts from {title}.",
  "sess.paper_new_cards": "It starts with the {n} Item Bank card(s) chosen for this lesson, ready to edit. Classroom use does not spend a card's monthly or semester allocation; the use is still recorded, and whoever assembles M1, M2 or SA1 is warned that this grade has already seen it.",
  "sess.paper_new_blank": "Opens blank — book and practice citations are references, not printable blocks.",
  "sess.paper_open": "Open",
  "sess.paper_print": "Print",
  "sess.paper_marks": "{n} marks",
  "sess.paper_marks_one": "1 mark",
  "sess.paper_blocks": "{n} blocks",
  "sess.paper_blocks_one": "1 block",
  "sess.paper_from": "From {title}",
  "sess.paper_exact": "{n} exactly as imported",
  "sess.paper_edited": "{n} edited after import",
  "sess.paper_written": "{n} written by the teacher",
  "sess.paper_empty_blocks": "Nothing in it yet.",
  "sess.paper_cards": "Item Bank cards",
  "sess.stale": "This lesson changed since you opened it. The latest is shown.",
  "classes.add": "Add a class",
  "classes.name": "Class name",
  "classes.level": "Level",
  "classes.add_button": "Add class",
  "roster.students": "Students",
  "roster.empty": "No students yet. Paste the class list below.",
  "roster.add_title": "Add students",
  "roster.paste_hint": "Paste the class list — one name per line. You can paste straight from a spreadsheet.",
  "roster.placeholder": "One name per line…",
  "roster.add_button": "Add students",
  "roster.rename": "Rename",
  "roster.remove_confirm": "Remove? Tap again",

  // ── calendar ──
  "cal.title": "School Calendar",
  "cal.import": "Import from Excel",
  "cal.this_month": "This month",
  "nav.classes": "Classes",
  "nav.classes_sub": "Every class, its teacher and its periods per week.",
  "cal.view_week": "Week",
  "cal.view_month": "Month",
  "cal.view_term": "Term",
  "cal.view_year": "Year",
  "cal.today": "Today",
  "cal.prev": "Previous",
  "cal.next": "Next",
  "cal.your_lessons": "Your lessons this week",
  "cal.your_lessons_none": "No lessons planned this week yet.",
  "cal.plan_this_week": "Plan a lesson for this week",
  "cal.add": "＋ Add",
  "cal.upcoming": "Upcoming",
  "cal.nothing_upcoming": "Nothing coming up.",
  "cal.before_term": "Before term",
  "cal.add_title": "Add to the calendar",
  "cal.date": "Date",
  "cal.label": "What is it?",
  "cal.label_placeholder": "e.g. Khmer New Year holiday",
  "cal.type": "Type",
  "cal.add_button": "Add entry",
  "cal.removed_toast": "Removed “{label}”.",
  "cal.undo": "Undo",
  "cal.restore": "Restore",
  "cal.recently_removed": "Recently removed",
  "cal.removed_hint": "Removed entries are kept and can be brought back.",
  "cal.until": "Until (optional)",
  "cal.until_hint": "Leave blank for a single day.",
  "cal.key_toggle": "Key event (shown on Home + the Year view)",
  "cal.grades": "Grades",
  "cal.teaching_day": "Teaching happens this day",
  "cal.key_dates": "Key dates this year",
  "cal.edited_by": "edited by {name} · {when}",

  // ── observations (teacher view) ──
  "obs.title_teacher": "My observations",
  "obs.title_lead": "Observation records",
  "obs.hint_teacher": "Your observation and marking-check records — discussed 1-on-1, private to you and the Lead.",
  "obs.hint_lead": "Every observation and marking-check record, newest first. Start a new one from a teacher's page in Team.",
  "obs.kind_classroom": "Classroom observation",
  "obs.kind_marking": "Book & worksheet check",
  "obs.chip_ready": "Ready to review",
  "obs.chip_acknowledged": "Acknowledged ✓",
  "obs.empty": "No observation records yet. They'll appear here after an observation has been discussed with you.",

  // ── settings ──
  "settings.title": "Settings",
  "settings.language": "Language",
  "settings.passcode_title": "Change passcode",
  "settings.passcode_hint": "Pick something you'll remember. If you forget it, your Lead can reset it for you.",
  "settings.current": "Current passcode",
  "settings.new": "New passcode",
  "settings.new_placeholder": "at least 4 characters",
  "settings.updated": "Passcode updated.",
  "settings.update_button": "Update passcode",

  // ── settings · AI keys (③ slice 2 / BYOK) ──
  "settings.keys_title": "AI keys",
  "settings.keys_hint": "Your own free Gemini key powers your speaking practice. Get one at aistudio.google.com, paste it here — after saving, only the last 4 characters ever show again.",
  "settings.keys_add_label": "API key",
  "settings.keys_paste_placeholder": "paste your key here",
  "settings.keys_nickname": "Nickname (optional)",
  "settings.keys_nickname_placeholder": "e.g. my gmail key",
  "settings.keys_add_button": "Save key",
  "settings.keys_saved": "Key saved. It's stored safely — only you can use it.",
  "settings.keys_none": "No keys yet.",
  "settings.keys_unnamed": "Key",
  "settings.keys_status_working": "Working",
  "settings.keys_status_resting": "Resting — free limit reached, back soon",
  "settings.keys_status_disabled": "Off",
  "settings.keys_enable": "Turn on",
  "settings.keys_disable": "Turn off",

  // ── plan fork (④ — two roads to the same three cells) ──
  "fork.title": "Plan a lesson",
  "fork.hint": "Two roads to the same thinking — pick what fits today.",
  "fork.capture": "Just capture it",
  "fork.capture_sub": "Type the three cells — about 10 minutes. Not recorded.",
  "fork.speak": "Talk it through",
  "fork.speak_sub": "Speak with a thinking partner first, then type your plan.",
  "fork.speak_recorded": "Recorded — you and your Lead can listen later.",

  // ── recording consent (docs/RECORDING_NOTICE.md §2 — KH review is a SHIP GATE) ──
  "consent.title": "Before you talk it through",
  "consent.intro": "When you talk through a lesson with EDEN instead of typing it, EDEN records the conversation. Here's exactly what that means, so there are no surprises:",
  "consent.what_saved": "What's saved: your spoken audio, a written transcript, and the lesson decisions you reach.",
  "consent.who": "Who can see and hear it: you and your Lead (HOD). No one else — not other teachers, not the team dashboard, not any report or export by default.",
  "consent.for": "What it's for: coaching and support — helping your Lead help you teach. It is not a score, a grade, or a ranking. EDEN never rates your thinking.",
  "consent.lead_listens": "You can see when your Lead listens. Every time your Lead opens your session, it's logged — so oversight is visible to you too, not hidden.",
  "consent.where": "Where it's kept: securely, in EDEN's coaching store and audio storage — kept separate from the team's coordination data, never saved onto your phone.",
  "consent.typing": "Typing is always an option. Prefer not to be recorded for a lesson? Fast-capture (type the three cells) is always available and is not recorded.",
  "consent.agree": "By continuing to the talk-through, you confirm you've read this and agree to be recorded.",
  "consent.start": "I understand — start talking",
  "consent.type_instead": "Not now — type instead",

  // ── speak (the talk-it-through session) ──
  "speak.title": "Talk it through",
  "speak.frame_hint": "Set the lesson first — then talk.",
  "speak.start_button": "Start the talk-through",
  "speak.banner": "Recording · This talk-through is saved (audio + notes) so your Lead can listen later — only you and your Lead, never the team. It's for coaching, not scoring.",
  "speak.connecting": "Getting your thinking partner…",
  "speak.tap_to_start": "When you're ready, tap to talk.",
  "speak.tap_to_talk": "Tap to talk",
  "speak.done": "Done",
  "speak.listening": "Listening…",
  "speak.thinking": "Thinking…",
  "speak.you_decided": "You've decided",
  "speak.escape_typing": "Prefer not to be recorded? Switch to typing — nothing here is saved.",
  "speak.cell_prior": "Prior knowledge",
  "speak.cell_misconception": "The misconception",
  "speak.cell_check": "The check",
  "speak.more_help": "More help",
  "speak.text_toggle": "Type instead",
  "speak.text_placeholder": "Type to your thinking partner…",
  "speak.send": "Send",
  "speak.end": "Finish",
  "speak.turn_failed": "That didn't go through. Tap to talk and try that again — your thinking so far is saved.",
  "speak.help_on": "Help is on",
  "speak.hard_fail": "Something went quiet on our side. Your thinking is saved — try again in a moment.",
  "speak.resting": "The AI is resting (free limit reached). Your thinking so far is saved — try again a little later.",
  "speak.no_keys": "Speaking practice runs on your own free Gemini key. Get one at aistudio.google.com, add it in Settings, then come back here.",
  "speak.no_keys_link": "Add a key in Settings",
  "speak.no_keys_title": "One thing first — your key",
  "speak.try_again": "Try again",
  "speak.summary_title": "Your thinking, gathered",
  "speak.summary_hint": "Read-only — the plan stays yours. Read it, then type the plan in your own words.",
  "speak.insights": "Insights",
  "speak.key_points": "Key points for the lesson",
  "speak.your_words": "Your words, polished",
  "speak.summary_pending": "Your summary is on its way — check back from this page in a little while.",
  "speak.now_type": "Now type your plan",
  "speak.talk_again": "Talk it through again",
  "speak.ask_lead": "Ask my Lead for grammar help on this",
  "speak.asked_lead": "Your Lead will take a look.",

  // ── 2026-09-30 redesign: the new navigation + screens (plain words, short) ──
  "nav.home": "Home",
  "nav.year_plan": "Year plan",
  "nav.tab_year_plan": "Year plan",
  "nav.year_plan_sub": "Every week of the year, with its worksheet and pack.",
  "nav.books": "Books",
  "nav.books_sub": "Every book for your grade: pages, audio and answer keys.",
  "nav.pdf_books": "PDF BOOKS",
  "nav.pdf_books_sub": "Read scans, review text and export your own PDF books.",
  "nav.my_plans": "My plans",
  "nav.my_plans_sub": "The lesson plans you have written.",
  "nav.tests": "Worksheets & tests",
  "nav.tests_sub": "Make a worksheet or test to print.",
  "nav.calendar": "Calendar",
  "nav.calendar_sub": "School dates, tests and holidays.",
  "nav.team": "Team",
  "nav.team_sub": "Plans waiting for you, and who has planned this week.",
  "nav.setup": "Setup",
  "nav.setup_sub": "People, classes and the school year.",
  "nav.lead_group": "Lead",
  "nav.my_classes": "My classes",
  "nav.my_classes_sub": "Your classes and student lists.",
  "nav.observations_sub": "Observation records, discussed 1-on-1.",
  "nav.board": "Team board",
  "nav.board_sub": "Shared team projects.",
  "lesson.worksheet": "Worksheet",
  "lesson.pack": "Teacher Pack",
  "lesson.open": "Open the full lesson",
  "lesson.plan": "Plan this lesson",
  "lesson.continue": "Continue plan",
  "lesson.view_plan": "View plan",
  "lesson.no_sheet": "No worksheet for this week yet.",
  "lesson.no_map": "Grades 11–12 are not in the Term 1 pilot yet — you can still plan your own lesson.",
  "lesson.no_grade": "Name the class with its grade (for example “G6A”) to see its lessons.",
  "lesson.preview": "Term {term} starts {date} — here is Week 1 so you can prepare.",

  // ── settings · coaching transparency (DR-001 honesty rail) ──
  "settings.coach_reads_title": "Your coaching sessions",
  "settings.coach_reads_hint": "When your Lead reads or listens to a talk-it-through, it shows here — oversight is visible to you too.",
  "settings.coach_read": "Your Lead read a session",
  "settings.coach_listened": "Your Lead listened to a session",
  "settings.coach_remove": "Ask your Lead to remove a session",
};

export type StringKey = keyof typeof en;

/** EDEN is English-only. Khmer is retained as a display FONT (for imported student names),
 *  never as a UI language — so there is no toggle and no second string table. */
export function initLang(): void {
  document.documentElement.lang = "en";
}

/**
 * Translate a key. `{param}` placeholders are substituted and HTML-ESCAPED here, so user data
 * (names, topics, labels) is safe to pass raw — the string templates themselves are trusted
 * app constants (exactly like the inline literals they replaced).
 */
export function t(key: StringKey, params?: Record<string, string | number>): string {
  let s: string = en[key];
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      s = s.split(`{${k}}`).join(esc(String(v)));
    }
  }
  return s;
}
